<template>
  <div class="rectification-page">
    <header class="page-head">
      <div>
        <h2>整改事项</h2>
        <p>{{ pendingCount }} 项未完成</p>
      </div>
      <button v-if="canEdit" class="primary-button" type="button" @click="composerOpen = true">＋ 添加整改意见</button>
    </header>

    <section class="list-panel">
      <div class="list-toolbar">
        <div class="status-tabs" role="tablist" aria-label="整改状态">
          <button type="button" :class="{ active: statusFilter === 'ALL' }" @click="statusFilter = 'ALL'">全部 {{ issueTotal }}</button>
          <button type="button" :class="{ active: statusFilter === 'PENDING' }" @click="statusFilter = 'PENDING'">未完成 {{ pendingCount }}</button>
          <button type="button" :class="{ active: statusFilter === 'COMPLETED' }" @click="statusFilter = 'COMPLETED'">已完成 {{ completedCount }}</button>
          <button type="button" :class="{ active: statusFilter === 'VOID' }" @click="statusFilter = 'VOID'">已作废 {{ voidCount }}</button>
        </div>
        <button class="refresh-button" type="button" title="刷新" aria-label="刷新" :disabled="loading" @click="loadBatches">↻</button>
      </div>

      <div v-if="loading" class="page-state">正在读取...</div>
      <div v-else-if="batches.length" class="batch-list">
        <article v-for="batch in visibleBatches" :key="batch.id" class="batch-card">
          <header class="batch-head">
            <div>
              <strong>{{ batch.completedCount }}/{{ batch.issueCount }} 已完成</strong>
              <small>{{ batch.creatorDisplayName || "内部成员" }} · {{ formatTime(batch.createdAt) }}</small>
            </div>
            <span class="batch-head__actions">
              <button class="source-button" type="button" @click="copyBatchAssets(batch)">
                {{ copiedBatchId === batch.id ? "已复制地址" : "复制本批地址" }}
              </button>
              <button class="source-button" type="button" @click="toggleSource(batch.id)">
                {{ expandedSources[batch.id] ? "收起原问题" : "查看原问题" }}
              </button>
            </span>
          </header>
          <p v-if="expandedSources[batch.id]" class="source-text">{{ batch.sourceText }}</p>
          <div v-if="batch.loading" class="batch-loading">正在读取事项...</div>
          <div v-else class="issue-list">
            <section v-for="group in groupedItems(batch)" :key="group.name" class="interface-group">
              <header class="interface-head">
                <strong>{{ group.name }}</strong>
                <small>{{ group.completedCount }}/{{ group.items.length }} 已完成</small>
              </header>
              <div v-for="issue in group.items" :key="issue.id" class="issue-row" :class="{ done: isCompleted(issue), voided: isVoided(issue) }">
                <div class="issue-row__main">
                  <input
                    type="checkbox"
                    :checked="isCompleted(issue)"
                    :disabled="!canComplete || completingId === issue.id || isVoided(issue)"
                    @change="toggleCompletion(issue, $event.target.checked)"
                  />
                  <span class="issue-content">{{ issue.issueContent }}</span>
                  <span class="issue-meta">
                    <small v-if="isCompleted(issue)">{{ issue.completedByDisplayName || "内部成员" }} · {{ formatTime(issue.completedAt) }}</small>
                    <small v-else-if="isVoided(issue)">已作废</small>
                    <small v-else>未完成</small>
                    <button
                      v-if="canComplete"
                      class="issue-action"
                      type="button"
                      :disabled="completingId === issue.id"
                      @click="toggleVoid(issue)"
                    >{{ isVoided(issue) ? "恢复" : "作废" }}</button>
                  </span>
                </div>
                <div class="issue-row__assets">
                  <div class="attachment-strip">
                    <button
                      v-for="attachment in activeAttachments(issue)"
                      :key="attachment.id"
                      class="attachment-thumb"
                      type="button"
                      :title="attachmentTitle(attachment)"
                      @click="openAttachmentPreview(attachment)"
                    >
                      <img :src="resolveAssetUrl(attachment.fileUrl)" :alt="attachment.assetName" loading="lazy" />
                      <span class="attachment-thumb__tag">{{ attachmentKindLabel(attachment) }}</span>
                    </button>
                    <span v-if="!activeAttachments(issue).length" class="attachment-empty">未上传界面图或图标</span>
                  </div>
                  <div class="asset-actions">
                    <template v-if="canEdit">
                      <button class="asset-button" type="button" :disabled="uploadingIssueId === issue.id" @click="chooseAttachments(issue, 'IMAGE')">
                        {{ uploadingIssueId === issue.id ? "上传中..." : "＋ 界面图" }}
                      </button>
                      <button class="asset-button" type="button" :disabled="uploadingIssueId === issue.id" @click="chooseAttachments(issue, 'ICON')">＋ 图标</button>
                    </template>
                    <button class="asset-button asset-button--ghost" type="button" @click="copyIssueAssets(issue)">
                      {{ copiedIssueId === issue.id ? "已复制地址" : "复制地址" }}
                    </button>
                    <button
                      v-if="attachmentHistory(issue).length"
                      class="asset-button asset-button--ghost"
                      type="button"
                      @click="openAssetHistory(issue)"
                    >历史 {{ attachmentHistory(issue).length }}</button>
                  </div>
                </div>
              </div>
            </section>
            <div v-if="!filteredItems(batch).length" class="empty-filter">当前没有符合条件的事项</div>
          </div>
        </article>
      </div>
      <div v-else class="page-state page-state--empty">暂无整改事项</div>
    </section>

    <div v-if="composerOpen" class="modal-backdrop" @click.self="composerOpen = false">
      <section class="composer-dialog" role="dialog" aria-modal="true" aria-label="添加整改意见">
        <header class="composer-dialog__head">
          <h3>添加整改意见</h3>
          <button type="button" title="关闭" aria-label="关闭" @click="composerOpen = false">×</button>
        </header>
        <textarea
          v-model.trim="problemText"
          maxlength="5000"
          placeholder="填写原始问题后可使用 AI 整理（可选）"
          aria-label="需要整改的问题"
        ></textarea>
        <div class="composer-actions">
          <span>{{ problemText.length }}/5000</span>
          <span class="composer-actions__buttons">
            <button class="plain-button" type="button" :disabled="draftItems.length >= 50" @click="addDraftItem">直接添加事项</button>
            <button class="primary-button" type="button" :disabled="drafting || !problemText" @click="generateDraft">
              {{ drafting ? "AI 整理中..." : "AI 整理事项" }}
            </button>
          </span>
        </div>

        <div v-if="draftItems.length" class="draft-list">
          <div class="draft-list__head">
            <strong>待保存事项</strong>
            <button class="text-button" type="button" title="添加事项" aria-label="添加事项" @click="addDraftItem">＋</button>
          </div>
          <div v-for="(item, index) in draftItems" :key="item.key" class="draft-row">
            <span>{{ index + 1 }}</span>
            <input v-model.trim="item.interfaceName" maxlength="96" placeholder="所属界面" :aria-label="`第 ${index + 1} 条事项所属界面`" />
            <textarea v-model.trim="item.content" rows="2" maxlength="500" :aria-label="`第 ${index + 1} 条整改事项`"></textarea>
            <button type="button" title="删除事项" aria-label="删除事项" @click="removeDraftItem(index)">×</button>
          </div>
          <div class="draft-list__foot">
            <span>{{ validDraftCount }} 项可保存</span>
            <button class="primary-button" type="button" :disabled="saving || !validDraftCount" @click="saveDraft">
              {{ saving ? "保存中..." : "保存事项" }}
            </button>
          </div>
        </div>
      </section>
    </div>
    <input ref="assetInput" hidden multiple type="file" accept=".png,.jpg,.jpeg,.gif,.webp" @change="handleAssetFiles" />

    <div v-if="historyIssueId" class="modal-backdrop" @click.self="historyIssueId = null">
      <section class="history-dialog" role="dialog" aria-modal="true" aria-label="附件历史记录">
        <header class="composer-dialog__head">
          <h3>附件历史记录</h3>
          <button type="button" title="关闭" aria-label="关闭" @click="historyIssueId = null">×</button>
        </header>
        <p class="history-issue">{{ historyIssue ? historyIssue.issueContent : "" }}</p>
        <div v-if="!historyItems.length" class="history-empty">这个整改条例还没有上传记录</div>
        <ul v-else class="history-list">
          <li v-for="item in historyItems" :key="item.id" :class="{ 'history-item--removed': item.status !== 'ACTIVE' }">
            <button class="history-thumb" type="button" :title="attachmentTitle(item)" @click="openAttachmentPreview(item)">
              <img :src="resolveAssetUrl(item.fileUrl)" :alt="item.assetName" loading="lazy" />
            </button>
            <div class="history-copy">
              <strong>{{ item.assetName || item.originalName || "未命名附件" }}</strong>
              <span>{{ attachmentKindLabel(item) }} · {{ formatFileSize(item.fileSize) }}</span>
              <span>{{ item.creatorDisplayName || "内部成员" }} 上传于 {{ formatTime(item.createdAt) }}</span>
              <span v-if="item.note" class="history-note">备注：{{ item.note }}</span>
              <span v-if="item.status !== 'ACTIVE'" class="history-removed">
                已移除 · {{ item.removedByDisplayName || "内部成员" }} · {{ formatTime(item.removedAt) }}
              </span>
            </div>
            <div class="history-actions">
              <a :href="resolveAssetUrl(item.fileUrl)" :download="item.originalName || item.assetName" title="下载" aria-label="下载">↓</a>
              <button v-if="canEdit && item.status === 'ACTIVE'" class="history-remove" type="button" @click="removeAttachment(item)">移除</button>
            </div>
          </li>
        </ul>
      </section>
    </div>
    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import { hasAdminPermission } from "../utils/adminSession"
import {
  fetchRectificationBatches,
  fetchRectificationItems,
  generateRectificationDraft,
  removeRectificationAttachment,
  saveRectificationBatch,
  setRectificationCompleted,
  setRectificationStatus,
  uploadRectificationAttachments
} from "../api/officeRectification"
import { resolveApiOrigin, resolveBackendAssetUrl } from "../utils/apiEndpoint"

export default {
  name: "OfficeRectificationView",
  data() {
    return {
      problemText: "",
      draftItems: [],
      composerOpen: false,
      batches: [],
      expandedSources: {},
      statusFilter: "ALL",
      loading: false,
      drafting: false,
      saving: false,
      completingId: null,
      uploadingIssueId: null,
      uploadingAssetKind: "IMAGE",
      assetTargetIssueId: null,
      copiedIssueId: null,
      copiedBatchId: null,
      historyIssueId: null,
      issueCopyTimer: null,
      batchCopyTimer: null,
      draftKey: 0,
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  computed: {
    canEdit() {
      return hasAdminPermission("office:rectification:edit")
    },
    canComplete() {
      return hasAdminPermission("office:rectification:complete")
    },
    validDraftCount() {
      return this.draftItems.filter(item => item.content.trim()).length
    },
    issueTotal() {
      return this.batches.reduce((total, batch) => total + Number(batch.issueCount || 0), 0)
    },
    completedCount() {
      return this.batches.reduce((total, batch) => total + Number(batch.completedCount || 0), 0)
    },
    voidCount() {
      return this.batches.reduce((total, batch) => total + (batch.items || []).filter(item => this.isVoided(item)).length, 0)
    },
    pendingCount() {
      return this.issueTotal - this.completedCount - this.voidCount
    },
    visibleBatches() {
      return this.batches.filter(batch => this.statusFilter === "ALL" || this.filteredItems(batch).length)
    },
    historyIssue() {
      if (!this.historyIssueId) return null
      return this.findIssue(this.historyIssueId)
    },
    historyItems() {
      return this.attachmentHistory(this.historyIssue).slice().reverse()
    }
  },
  created() {
    this.loadBatches()
  },
  methods: {
    async loadBatches() {
      this.loading = true
      try {
        const response = await fetchRectificationBatches()
        const batches = Array.isArray(response.data) ? response.data : []
        this.batches = batches.map(batch => ({ ...batch, items: [], loading: true }))
        await Promise.all(this.batches.map(batch => this.loadItems(batch)))
      } catch (error) {
        this.showToast(error.message || "整改事项读取失败", true)
      } finally {
        this.loading = false
      }
    },
    async loadItems(batch) {
      try {
        const response = await fetchRectificationItems(batch.id)
        batch.items = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        batch.items = []
        this.showToast(error.message || "整改事项读取失败", true)
      } finally {
        batch.loading = false
      }
    },
    async generateDraft() {
      if (!this.problemText) return
      this.drafting = true
      try {
        const response = await generateRectificationDraft({ problemText: this.problemText })
        const items = response.data && Array.isArray(response.data.items) ? response.data.items : []
        this.draftItems = items.map(item => typeof item === "string"
          ? this.newDraftItem("未分类界面", item)
          : this.newDraftItem(item.interfaceName, item.content))
        this.showToast(response.message || "AI 已整理事项")
      } catch (error) {
        this.showToast(error.message || "AI 整理失败", true)
      } finally {
        this.drafting = false
      }
    },
    newDraftItem(interfaceName = "", content = "") {
      this.draftKey += 1
      return { key: this.draftKey, interfaceName: interfaceName || "", content: content || "" }
    },
    addDraftItem() {
      if (this.draftItems.length < 50) this.draftItems.push(this.newDraftItem())
    },
    removeDraftItem(index) {
      this.draftItems.splice(index, 1)
    },
    async saveDraft() {
      const items = this.draftItems
        .filter(item => item.content.trim())
        .map(item => ({ interfaceName: item.interfaceName.trim() || "未分类界面", content: item.content.trim() }))
      if (!items.length) return
      this.saving = true
      try {
        const fallbackText = items.map(item => `${item.interfaceName}：${item.content}`).join("\n")
        await saveRectificationBatch({ problemText: this.problemText || fallbackText, items })
        this.problemText = ""
        this.draftItems = []
        this.composerOpen = false
        this.showToast("整改事项已保存")
        await this.loadBatches()
      } catch (error) {
        this.showToast(error.message || "整改事项保存失败", true)
      } finally {
        this.saving = false
      }
    },
    filteredItems(batch) {
      const items = Array.isArray(batch.items) ? batch.items : []
      return this.statusFilter === "ALL" ? items : items.filter(item => item.status === this.statusFilter)
    },
    groupedItems(batch) {
      const groups = new Map()
      this.filteredItems(batch).forEach(item => {
        const name = item.interfaceName || "未分类界面"
        if (!groups.has(name)) groups.set(name, [])
        groups.get(name).push(item)
      })
      return Array.from(groups, ([name, items]) => ({
        name,
        items,
        completedCount: items.filter(item => this.isCompleted(item)).length
      }))
    },
    isCompleted(issue) {
      return issue.status === "COMPLETED"
    },
    isVoided(issue) {
      return issue.status === "VOID"
    },
    async toggleCompletion(issue, completed) {
      await this.updateIssueStatus(issue, completed ? "COMPLETED" : "PENDING")
    },
    async toggleVoid(issue) {
      await this.updateIssueStatus(issue, this.isVoided(issue) ? "PENDING" : "VOID")
    },
    async updateIssueStatus(issue, status) {
      this.completingId = issue.id
      try {
        const response = status === "COMPLETED" || status === "PENDING"
          ? await setRectificationCompleted(issue.id, status === "COMPLETED")
          : await setRectificationStatus(issue.id, status)
        const updated = response.data
        this.batches.forEach(batch => {
          const index = batch.items.findIndex(item => item.id === issue.id)
          if (index >= 0) {
            this.$set(batch.items, index, updated)
            const completedDelta = (updated.status === "COMPLETED" ? 1 : 0) - (issue.status === "COMPLETED" ? 1 : 0)
            batch.completedCount = Number(batch.completedCount || 0) + completedDelta
          }
        })
      } catch (error) {
        this.showToast(error.message || "状态更新失败", true)
      } finally {
        this.completingId = null
      }
    },
    toggleSource(batchId) {
      this.$set(this.expandedSources, batchId, !this.expandedSources[batchId])
    },
    findIssue(issueId) {
      for (const batch of this.batches) {
        const issue = (batch.items || []).find(item => item.id === issueId)
        if (issue) return issue
      }
      return null
    },
    attachmentHistory(issue) {
      return issue && Array.isArray(issue.attachments) ? issue.attachments : []
    },
    activeAttachments(issue) {
      return this.attachmentHistory(issue).filter(item => item.status === "ACTIVE")
    },
    attachmentKindLabel(attachment) {
      return attachment && attachment.assetKind === "ICON" ? "图标" : "界面图"
    },
    attachmentTitle(attachment) {
      if (!attachment) return ""
      const name = attachment.assetName || attachment.originalName || "未命名附件"
      return `${this.attachmentKindLabel(attachment)}：${name}`
    },
    resolveAssetUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    absoluteAssetUrl(value) {
      const current = String(value || "").trim()
      if (!current || current.startsWith("data:") || current.startsWith("blob:")) return current
      if (/^https?:\/\//i.test(current)) return current
      if (current.startsWith("//")) return `${window.location.protocol}${current}`
      return `${resolveApiOrigin().replace(/\/$/, "")}/${current.replace(/^\/+/, "")}`
    },
    statusLabel(issue) {
      if (this.isCompleted(issue)) return "已完成"
      if (this.isVoided(issue)) return "已作废"
      return "未完成"
    },
    issueAssetText(issue) {
      const lines = [
        `界面：${issue.interfaceName || "未分类界面"}`,
        `整改事项：${issue.issueContent || ""}`,
        `状态：${this.statusLabel(issue)}`
      ]
      const active = this.activeAttachments(issue)
      if (active.length) {
        lines.push("", "界面图与图标（数据库名称与地址）：")
        active.forEach(attachment => {
          lines.push(`- ${this.attachmentKindLabel(attachment)}名称：${attachment.assetName || attachment.originalName || "未命名附件"}`)
          lines.push(`  ${this.attachmentKindLabel(attachment)}地址：${this.absoluteAssetUrl(attachment.fileUrl)}`)
        })
      } else {
        lines.push("", "界面图与图标：（暂无上传）")
      }
      return lines.join("\n")
    },
    batchAssetText(batch) {
      const items = this.filteredItems(batch)
      if (!items.length) return ""
      const header = `整改清单（${this.formatTime(batch.createdAt) || "时间未知"} · ${batch.creatorDisplayName || "内部成员"} 创建 · 共 ${items.length} 条）`
      return [header, ...items.map(issue => this.issueAssetText(issue))].join("\n\n")
    },
    openAttachmentPreview(attachment) {
      if (!attachment) return
      window.open(this.resolveAssetUrl(attachment.fileUrl), "_blank", "noopener")
    },
    openAssetHistory(issue) {
      this.historyIssueId = issue.id
    },
    chooseAttachments(issue, assetKind) {
      this.assetTargetIssueId = issue.id
      this.uploadingAssetKind = assetKind === "ICON" ? "ICON" : "IMAGE"
      const input = this.$refs.assetInput
      if (!input) return
      input.value = ""
      input.click()
    },
    async handleAssetFiles(event) {
      const files = Array.from(event.target.files || [])
      const issueId = this.assetTargetIssueId
      if (!files.length || !issueId) return
      const issue = this.findIssue(issueId)
      if (!issue) return
      this.uploadingIssueId = issueId
      try {
        const response = await uploadRectificationAttachments(issueId, files, this.uploadingAssetKind)
        const uploaded = Array.isArray(response.data) ? response.data : []
        if (uploaded.length) {
          const existing = this.attachmentHistory(issue).slice()
          uploaded.forEach(item => {
            const index = existing.findIndex(record => record.id === item.id)
            if (index >= 0) existing.splice(index, 1, item)
            else existing.push(item)
          })
          this.$set(issue, "attachments", existing)
        }
        this.showToast(`${uploaded.length} 个${this.uploadingAssetKind === "ICON" ? "图标" : "界面图"}已上传`)
      } catch (error) {
        this.showToast(error.message || "附件上传失败", true)
      } finally {
        this.uploadingIssueId = null
        this.assetTargetIssueId = null
        event.target.value = ""
      }
    },
    async removeAttachment(attachment) {
      if (!attachment) return
      this.uploadingIssueId = attachment.issueId
      try {
        const response = await removeRectificationAttachment(attachment.id)
        const updated = response.data
        const issue = this.findIssue(attachment.issueId)
        if (issue && updated) {
          const existing = this.attachmentHistory(issue).slice()
          const index = existing.findIndex(record => record.id === updated.id)
          if (index >= 0) existing.splice(index, 1, updated)
          this.$set(issue, "attachments", existing)
        }
        this.showToast("附件已移除，历史记录仍然保留")
      } catch (error) {
        this.showToast(error.message || "附件移除失败", true)
      } finally {
        this.uploadingIssueId = null
      }
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const input = document.createElement("textarea")
      input.value = text
      input.setAttribute("readonly", "readonly")
      input.style.position = "fixed"
      input.style.opacity = "0"
      document.body.appendChild(input)
      input.select()
      const copied = document.execCommand("copy")
      document.body.removeChild(input)
      if (!copied) throw new Error("浏览器未允许复制")
    },
    async copyIssueAssets(issue) {
      if (!issue) return
      try {
        await this.copyText(this.issueAssetText(issue))
        this.copiedIssueId = issue.id
        clearTimeout(this.issueCopyTimer)
        this.issueCopyTimer = setTimeout(() => {
          if (this.copiedIssueId === issue.id) this.copiedIssueId = null
        }, 1800)
        this.showToast("整改事项与附件地址已复制")
      } catch (error) {
        this.showToast(error.message || "地址复制失败", true)
      }
    },
    async copyBatchAssets(batch) {
      try {
        const text = this.batchAssetText(batch)
        if (!text) throw new Error("当前没有可复制的整改事项")
        await this.copyText(text)
        this.copiedBatchId = batch.id
        clearTimeout(this.batchCopyTimer)
        this.batchCopyTimer = setTimeout(() => {
          if (this.copiedBatchId === batch.id) this.copiedBatchId = null
        }, 1800)
        this.showToast("本批整改事项与附件地址已复制")
      } catch (error) {
        this.showToast(error.message || "地址复制失败", true)
      }
    },
    formatFileSize(value) {
      const size = Number(value || 0)
      if (!size) return "未知大小"
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => { this.toast = "" }, 3000)
    },
    formatTime(value) {
      if (!value) return ""
      const date = new Date(String(value).replace(/-/g, "/"))
      if (Number.isNaN(date.getTime())) return value
      const pad = number => String(number).padStart(2, "0")
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.rectification-page { max-width: 1080px; margin: 0 auto; }
.page-head, .list-toolbar, .composer-actions, .draft-list__head, .draft-list__foot, .batch-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-head { margin-bottom: 18px; }
.page-head h2 { margin: 0; color: #1f2937; font-size: 22px; }
.page-head p { margin: 6px 0 0; color: #667085; font-size: 13px; }
.list-panel { border: 1px solid #e3e8ef; background: #fff; border-radius: 8px; }
.modal-backdrop { position: fixed; inset: 0; z-index: 90; display: grid; place-items: center; padding: 20px; background: rgba(22, 29, 36, .42); }
.composer-dialog { width: min(760px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(12, 18, 24, .2); }
.composer-dialog__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.composer-dialog__head h3 { margin: 0; color: #1f2937; font-size: 17px; }
.composer-dialog__head button { border: 0; background: transparent; color: #68747f; font-size: 22px; cursor: pointer; }
.composer-dialog textarea { display: block; box-sizing: border-box; width: 100%; min-height: 154px; padding: 12px; resize: vertical; border: 1px solid #d8e0ea; border-radius: 6px; color: #263238; font: inherit; line-height: 1.65; outline: none; }
.composer-dialog textarea:focus, .draft-row textarea:focus, .draft-row input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #dbeafe; }
.composer-actions { margin-top: 10px; }
.composer-actions__buttons { display: flex; gap: 8px; }
.composer-actions > span, .draft-list__foot > span { color: #8a96a8; font-size: 12px; }
.primary-button { min-height: 34px; padding: 0 14px; border: 0; border-radius: 6px; background: #2563eb; color: #fff; cursor: pointer; font: inherit; }
.plain-button { min-height: 34px; padding: 0 12px; border: 1px solid #d3dae0; border-radius: 6px; background: #fff; color: #53606c; cursor: pointer; font: inherit; }
.primary-button:disabled, .plain-button:disabled, .refresh-button:disabled, .issue-action:disabled { cursor: not-allowed; opacity: .55; }
.draft-list { margin-top: 16px; border-top: 1px solid #edf1f5; }
.draft-list__head { padding: 14px 0 8px; color: #344054; font-size: 14px; }
.text-button, .draft-row button, .refresh-button, .source-button { border: 0; background: transparent; color: #2563eb; cursor: pointer; font: inherit; }
.text-button { font-size: 20px; line-height: 20px; }
.draft-row { display: grid; grid-template-columns: 24px 150px minmax(0, 1fr) 28px; gap: 8px; align-items: start; margin-top: 8px; }
.draft-row > span { color: #8a96a8; font-size: 13px; text-align: center; }
.draft-row textarea, .draft-row input { width: 100%; min-width: 0; box-sizing: border-box; padding: 7px 9px; border: 1px solid #d8e0ea; border-radius: 5px; color: #263238; font: inherit; line-height: 1.5; outline: none; }
.draft-row textarea { min-height: 52px; resize: vertical; }
.draft-row input { height: 38px; }
.draft-row button { color: #98a2b3; font-size: 20px; }
.draft-list__foot { margin-top: 12px; }
.list-panel { overflow: hidden; }
.list-toolbar { min-height: 56px; padding: 0 14px; border-bottom: 1px solid #edf1f5; }
.status-tabs { display: flex; gap: 2px; }
.status-tabs button { min-height: 32px; padding: 0 10px; border: 0; border-radius: 5px; background: transparent; color: #667085; cursor: pointer; font: inherit; font-size: 13px; }
.status-tabs button.active { background: #eaf2ff; color: #1d4ed8; font-weight: 600; }
.refresh-button { width: 32px; height: 32px; border: 1px solid #d8e0ea; border-radius: 5px; color: #667085; font-size: 18px; }
.batch-list { padding: 14px; }
.batch-card { border: 1px solid #e5eaf0; border-radius: 7px; overflow: hidden; }
.batch-card + .batch-card { margin-top: 12px; }
.batch-head { min-height: 52px; padding: 0 14px; background: #fbfcfe; }
.batch-head strong { display: block; color: #344054; font-size: 14px; }
.batch-head small { display: block; margin-top: 3px; color: #98a2b3; font-size: 12px; }
.source-button { font-size: 12px; white-space: nowrap; }
.source-text { margin: 0; padding: 10px 14px; border-top: 1px solid #edf1f5; background: #f8fafc; color: #667085; white-space: pre-wrap; font-size: 13px; line-height: 1.6; }
.issue-list { padding: 0 14px 4px; }
.interface-group + .interface-group { border-top: 1px solid #e5eaf0; }
.interface-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 0 5px; }
.interface-head strong { color: #344054; font-size: 14px; }
.interface-head small { color: #98a2b3; font-size: 12px; }
.issue-row { padding: 8px 0; border-bottom: 1px solid #f0f3f6; }
.issue-row:last-child { border-bottom: 0; }
.issue-row__main { display: grid; grid-template-columns: 18px minmax(0, 1fr) auto; gap: 10px; align-items: center; min-height: 32px; }
.issue-row input { width: 16px; height: 16px; accent-color: #2563eb; cursor: pointer; }
.issue-row__assets { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 12px; margin: 6px 0 0 28px; }
.attachment-strip { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.attachment-thumb { position: relative; width: 74px; height: 56px; padding: 0; overflow: hidden; border: 1px solid #e1e7ef; border-radius: 6px; background: #f6f8fb; cursor: pointer; }
.attachment-thumb img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.attachment-thumb__tag { position: absolute; left: 0; bottom: 0; padding: 1px 5px; border-radius: 0 6px 0 0; background: rgba(29, 78, 216, .82); color: #fff; font-size: 11px; }
.attachment-empty { color: #b0b8c4; font-size: 12px; }
.asset-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.asset-button { min-height: 28px; padding: 0 10px; border: 1px solid #d8e0ea; border-radius: 14px; background: #fff; color: #4b5763; cursor: pointer; font: inherit; font-size: 12px; }
.asset-button:hover:not(:disabled) { border-color: #9fbcf5; color: #1d4ed8; }
.asset-button:disabled { cursor: not-allowed; opacity: .6; }
.asset-button--ghost { border-color: #e4e9f0; background: #f7f9fc; color: #5b6673; }
.batch-head__actions { display: flex; align-items: center; gap: 12px; }
.issue-content { color: #344054; font-size: 14px; line-height: 1.45; }
.issue-row.done .issue-content { color: #98a2b3; text-decoration: line-through; }
.issue-row.voided .issue-content { color: #98a2b3; text-decoration: line-through; }
.issue-row small { color: #98a2b3; font-size: 12px; white-space: nowrap; }
.issue-meta { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.issue-action { border: 0; background: transparent; color: #a64438; cursor: pointer; font: inherit; font-size: 12px; }
.voided .issue-action { color: #2563eb; }
.batch-loading, .empty-filter, .page-state { padding: 34px 16px; color: #98a2b3; text-align: center; font-size: 13px; }
.page-state--empty { padding: 68px 16px; }
.toast { position: fixed; right: 24px; bottom: 24px; z-index: 120; max-width: 340px; padding: 11px 14px; border-radius: 6px; background: #274b3f; color: #fff; font-size: 12px; box-shadow: 0 10px 26px rgba(20, 37, 31, .2); }
.toast--error { background: #a64438; }
.history-dialog { width: min(620px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(12, 18, 24, .2); }
.history-issue { margin: 0 0 14px; padding: 10px 12px; border-radius: 6px; background: #f7f9fc; color: #4b5763; font-size: 13px; line-height: 1.6; }
.history-empty { padding: 26px 0; color: #98a2b3; text-align: center; font-size: 13px; }
.history-list { display: flex; flex-direction: column; gap: 10px; margin: 0; padding: 0; list-style: none; }
.history-list li { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #edf1f5; border-radius: 7px; }
.history-list li.history-item--removed { background: #fbfbfc; }
.history-thumb { flex: none; width: 68px; height: 52px; padding: 0; overflow: hidden; border: 1px solid #e1e7ef; border-radius: 6px; background: #f6f8fb; cursor: pointer; }
.history-thumb img { width: 100%; height: 100%; object-fit: contain; background: #fff; }
.history-copy { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.history-copy strong { overflow: hidden; color: #344054; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.history-copy span { color: #8a96a8; font-size: 12px; line-height: 1.5; }
.history-copy .history-removed { color: #a64438; }
.history-copy .history-note { color: #5b6673; }
.history-actions { display: flex; flex: none; align-items: center; gap: 10px; }
.history-actions a { color: #2563eb; font-size: 16px; text-decoration: none; }
.history-remove { border: 0; background: transparent; color: #a64438; cursor: pointer; font: inherit; font-size: 12px; }
@media (max-width: 640px) {
  .rectification-page { margin: 0; }
  .composer-dialog { width: 100%; max-height: 100vh; padding: 16px; border-radius: 0; }
  .history-dialog { width: 100%; max-height: 100vh; padding: 16px; border-radius: 0; }
  .modal-backdrop { padding: 0; }
  .batch-list { padding: 10px; }
  .issue-row__main { grid-template-columns: 18px minmax(0, 1fr); padding: 2px 0; }
  .issue-meta { grid-column: 2; justify-content: flex-start; }
  .issue-row__assets { margin-left: 0; }
  .status-tabs button { padding: 0 7px; font-size: 12px; }
  .composer-actions { align-items: flex-end; }
  .composer-actions__buttons { flex-wrap: wrap; justify-content: flex-end; }
  .draft-row { grid-template-columns: 24px minmax(0, 1fr) 28px; }
  .draft-row input, .draft-row textarea { grid-column: 2; }
  .draft-row textarea { grid-row: 2; }
  .draft-row button { grid-column: 3; grid-row: 1; }
}
</style>
